// Orphans -> MySYS: labels MySYS Post sells on ByeLabel's carrier accounts (migration 084).
//
// MySYS pays ByeLabel the raw rate; carrier adjustments on MySYS labels are
// REPORTED to MySYS, not rebilled (Sahin 2026-11-01). The Excel export is the
// MySYS-facing file: USPS adjustments only (reason + amount), never ByeLabel's cost.
// FedEx / UPS are shown here for review only until the adjustment rule is decided.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { DownloadLink } from '../components/DownloadLink'
import { FMT_INT } from '../lib/money'

interface Summary {
  period: { from: string; to: string }
  coverage: { first_label: string | null; last_label: string | null; labels: number; last_change: string | null } | null
  labels: { carrier_name: string; labels: number; active: number; deleted: number; raw_rate_active: string }[]
  usps: { transaction_type: string; reason: string | null; lines: number; labels: number; amount: string }[]
}
interface Adjustment {
  mysys_id: string; token_name: string | null; service_name: string | null; track_number: string
  label_date: string; label_deleted: boolean; transaction_type: string; reason: string | null
  details: string | null; transaction_date: string; amount: string
}
interface FedexUps {
  mysys_id: string; carrier_name: string; token_name: string | null; service_name: string | null
  track_number: string; label_date: string; label_deleted: boolean; raw_rate: string | null
  carrier_total: string | null; invoices: number; charge_lines: string | null
}

const usd = (v: string | number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

function monthStart(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}
const today = () => new Date().toISOString().slice(0, 10)

async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url)
  if (!r.ok) throw new Error(`${r.status} ${(await r.json().catch(() => ({}))).error ?? ''}`)
  return r.json()
}

export function MysysReport() {
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(today())
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to
  const qs = `from=${from}&to=${to}`

  const summary = useQuery<Summary>({ queryKey: ['mysys-summary', from, to], enabled: valid,
    queryFn: () => getJson(`/api/mysys/summary?${qs}`) })
  const adj = useQuery<{ rows: Adjustment[] }>({ queryKey: ['mysys-adj', from, to], enabled: valid,
    queryFn: () => getJson(`/api/mysys/adjustments?${qs}`) })
  const fu = useQuery<{ rows: FedexUps[] }>({ queryKey: ['mysys-fedex-ups', from, to], enabled: valid,
    queryFn: () => getJson(`/api/mysys/fedex-ups?${qs}`) })

  const cov = summary.data?.coverage
  const adjOnly = summary.data?.usps.filter(r => r.transaction_type === 'ADJUSTMENT') ?? []
  const adjTotal = adjOnly.reduce((s, r) => s + Number(r.amount), 0)

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-[12px] text-ink-500">From
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className="block h-9 px-2 rounded-lg border border-ink-200 text-[13px]" />
        </label>
        <label className="text-[12px] text-ink-500">To
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className="block h-9 px-2 rounded-lg border border-ink-200 text-[13px]" />
        </label>
        <div className="text-[12px] text-ink-400 self-center">
          Labels by MySYS sale date · adjustments by USPS billing date
        </div>
        {valid && (
          <DownloadLink href={`/api/mysys/export?${qs}`} download={`mysys-usps-adjustments-${from}-to-${to}.xlsx`}
            className="ml-auto h-9 px-3 rounded-lg border border-ink-200 text-ink-700 font-semibold inline-flex items-center text-[13px] hover:bg-ink-50">
            Excel for MySYS (USPS adjustments)
          </DownloadLink>
        )}
      </div>

      {cov && (
        <div className="text-[12px] text-ink-500">
          MySYS data: {FMT_INT.format(cov.labels)} labels sold {cov.first_label ?? '—'} to {cov.last_label ?? '—'}
          {cov.last_change && <> · last change {cov.last_change} (daily download, 02:30)</>}
          {' '}· MySYS keeps only 3 months, older labels cannot be checked.
        </div>
      )}
      {(summary.error || adj.error || fu.error) && (
        <div className="text-[13px] text-red-600">Could not load: {String(summary.error ?? adj.error ?? fu.error)}</div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-4">
          <div className="text-[12px] font-semibold text-ink-500 mb-2">Labels sold in the period</div>
          <table className="w-full text-[13px]">
            <thead><tr className="text-ink-400 text-left">
              <th className="py-1">Carrier</th><th className="text-right">Labels</th><th className="text-right">Deleted</th>
              <th className="text-right">Raw rate (active)</th>
            </tr></thead>
            <tbody>
              {(summary.data?.labels ?? []).map(r => (
                <tr key={r.carrier_name} className="border-t border-ink-100">
                  <td className="py-1">{r.carrier_name}</td>
                  <td className="text-right">{FMT_INT.format(r.labels)}</td>
                  <td className="text-right">{FMT_INT.format(r.deleted)}</td>
                  <td className="text-right">{usd(r.raw_rate_active)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card className="p-4">
          <div className="text-[12px] font-semibold text-ink-500 mb-2">USPS charges on MySYS labels billed in the period</div>
          <table className="w-full text-[13px]">
            <thead><tr className="text-ink-400 text-left">
              <th className="py-1">Type</th><th>Reason</th><th className="text-right">Labels</th><th className="text-right">Amount</th>
            </tr></thead>
            <tbody>
              {(summary.data?.usps ?? []).map(r => (
                <tr key={r.transaction_type + (r.reason ?? '')} className="border-t border-ink-100">
                  <td className="py-1">{r.transaction_type === 'REFUND' ? 'Refund (info)' : 'Adjustment'}</td>
                  <td>{r.reason ?? '—'}</td>
                  <td className="text-right">{FMT_INT.format(r.labels)}</td>
                  <td className="text-right">{usd(r.amount)}</td>
                </tr>
              ))}
              <tr className="border-t border-ink-200 font-semibold">
                <td className="py-1" colSpan={3}>Adjustments to report to MySYS</td>
                <td className="text-right">{usd(adjTotal)}</td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>

      <Card className="p-4">
        <div className="text-[12px] font-semibold text-ink-500 mb-2">
          USPS adjustment and refund lines ({FMT_INT.format(adj.data?.rows.length ?? 0)})
        </div>
        <div className="overflow-x-auto max-h-[480px]">
          <table className="w-full text-[12.5px]">
            <thead className="sticky top-0 bg-white"><tr className="text-ink-400 text-left">
              <th className="py-1 pr-3">USPS date</th><th className="pr-3">Type</th><th className="pr-3">Reason</th>
              <th className="pr-3">Tracking</th><th className="pr-3">Token</th><th className="pr-3">Label date</th>
              <th className="pr-3">Details</th><th className="text-right">Amount</th>
            </tr></thead>
            <tbody>
              {(adj.data?.rows ?? []).map((r, i) => (
                <tr key={i} className="border-t border-ink-100">
                  <td className="py-1 pr-3">{r.transaction_date}</td>
                  <td className="pr-3">{r.transaction_type === 'REFUND' ? 'Refund' : 'Adjustment'}</td>
                  <td className="pr-3">{r.reason ?? '—'}</td>
                  <td className="pr-3 font-mono">{r.track_number}</td>
                  <td className="pr-3">{r.token_name ?? '—'}</td>
                  <td className="pr-3">{r.label_date}{r.label_deleted && <span className="ml-1 text-ink-400">(deleted)</span>}</td>
                  <td className="pr-3 text-ink-500">{r.details ?? ''}</td>
                  <td className="text-right">{usd(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-4">
        <div className="text-[12px] font-semibold text-ink-500 mb-1">FedEx / UPS labels sold in the period</div>
        <div className="text-[12px] text-ink-400 mb-2">
          For review only — not in the MySYS file until the FedEx / UPS adjustment rule is decided.
          The carrier total includes every invoice line for the parcel.
        </div>
        <div className="overflow-x-auto max-h-[480px]">
          <table className="w-full text-[12.5px]">
            <thead className="sticky top-0 bg-white"><tr className="text-ink-400 text-left">
              <th className="py-1 pr-3">Label date</th><th className="pr-3">Carrier</th><th className="pr-3">Tracking</th>
              <th className="pr-3">Token</th><th className="pr-3">Service</th><th className="text-right pr-3">Raw rate</th>
              <th className="text-right pr-3">Carrier billed</th><th className="text-right pr-3">Difference</th><th>UPS lines</th>
            </tr></thead>
            <tbody>
              {(fu.data?.rows ?? []).map(r => {
                const diff = r.carrier_total != null && r.raw_rate != null ? Number(r.carrier_total) - Number(r.raw_rate) : null
                return (
                  <tr key={r.mysys_id} className="border-t border-ink-100">
                    <td className="py-1 pr-3">{r.label_date}{r.label_deleted && <span className="ml-1 text-ink-400">(deleted)</span>}</td>
                    <td className="pr-3">{r.carrier_name}</td>
                    <td className="pr-3 font-mono">{r.track_number}</td>
                    <td className="pr-3">{r.token_name ?? '—'}</td>
                    <td className="pr-3">{r.service_name ?? '—'}</td>
                    <td className="text-right pr-3">{usd(r.raw_rate)}</td>
                    <td className="text-right pr-3">{r.carrier_total == null ? 'not billed yet' : usd(r.carrier_total)}</td>
                    <td className={`text-right pr-3 ${diff != null && diff > 0 ? 'text-red-600 font-semibold' : ''}`}>
                      {diff == null ? '—' : usd(diff)}
                    </td>
                    <td className="text-ink-500">{r.charge_lines ?? ''}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
